// En tout premier : Tone.js rejette les samples du bundle natif (status 0 sous
// capacitor://), et le prechauffage part d'un constructeur execute a l'import.
import './utils/nativeAudioFetch';
// Juste apres, et avant tout module qui construit des noeuds Tone : le contexte
// audio elargi pour Android (no-op sur iOS et sur le web).
import './utils/androidToneContext';
import React from 'react';
import ReactDOM from 'react-dom/client';
import posthog from 'posthog-js';
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import reportWebVitals from './reportWebVitals';
import { setDeviceContext, trackNativeFirstOpen } from './utils/analytics';
import { bindAnalyticsIdentity } from './lib/analyticsIdentity';
import { ensureAnonSession } from './lib/supabaseClient';
import { configurePurchases } from './lib/purchases';
import { DataProvider } from './data/DataProvider';
import { isBrowserGated } from './utils/platformUtils';
import { getInviteCode } from './config/appLinks';
import { pendingInviteOAuthReturn, clearRememberedInvite } from './lib/inviteOAuth';
import GetAppPage from './components/GetAppPage';

// Initialize PostHog
if (process.env.REACT_APP_PUBLIC_POSTHOG_KEY && process.env.REACT_APP_PUBLIC_POSTHOG_KEY !== 'phc_placeholder') {
  posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY, {
    // Defaut sur l'UE, pas sur les US : la politique de confidentialite annonce
    // une ingestion europeenne, et un build ou la variable manque ne doit pas la
    // contredire en silence.
    api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
    autocapture: false,
    capture_pageview: false,
    persistence: 'localStorage',
  });
  setDeviceContext();
  // Dans le bloc d'init, pas a cote : hors de ce garde posthog n'est pas
  // initialise et chaque identify partirait dans le vide.
  bindAnalyticsIdentity();
}

// Apres l'init PostHog, avant tout rendu : ce que l'on veut dater, c'est le
// lancement lui-meme, pas l'ecran qui finit par s'afficher.
trackNativeFirstOpen();

const root = ReactDOM.createRoot(document.getElementById('root'));

// Pages de tournage /video (capture OBS pour YouTube) et /lick (apprentissage
// de licks pilote par Guitar Pro) : hors navigation et hors gate navigateur,
// chargees en lazy pour ne rien peser sur le bundle principal.
const isVideoPage = window.location.pathname.endsWith('/video');
const isLickPage = window.location.pathname.endsWith('/lick');
// Bac a sable UX : copie de /lick, meme parametres d'URL.
const isLickLabPage = window.location.pathname.endsWith('/licklab');
// Page d'installation : l'adresse qu'on partage a l'exterieur, et la cible du QR
// affiche sur desktop.
const isGetPage = window.location.pathname.endsWith('/get');
// Invitation : `?invite=CODE` sur la racine. Passe AVANT la gate navigateur,
// sinon un telephone, qui est justement l'appareil sur lequel le lien arrive,
// serait renvoye vers la page d'installation sans avoir pu racheter son code.
const inviteCode = getInviteCode();
// Retour d'Apple ou de Google pendant une invitation : la redirection atterrit sur
// l'origine nue, sans `?invite=`. Le code a ete mis de cote avant le depart, c'est lui
// qui ramene ici. Sans cette lecture, l'invite revient connecte sur l'app, son code
// jamais rachete.
const inviteOAuthReturn = inviteCode ? null : pendingInviteOAuthReturn();
// Un depart abandonne (retour arriere, onglet laisse ouvert) laisse le code de cote. Il
// ne doit pas attendre la prochaine connexion venue des reglages pour se greffer sur son
// retour OAuth et racheter sans que personne ne l'ait demande.
if (!inviteCode && !inviteOAuthReturn) clearRememberedInvite();
const StudioPage = isVideoPage
  ? React.lazy(() => import('./components/VideoPage'))
  : isLickPage
    ? React.lazy(() => import('./components/LickPage'))
    : isLickLabPage
      ? React.lazy(() => import('./components/LickLab'))
      : null;
const InvitePage =
  inviteCode || inviteOAuthReturn ? React.lazy(() => import('./components/InvitePage')) : null;

// Gate du staging web : la comparaison reste écrite en clair ici, et REACT_APP_STAGING
// doit exister (false par défaut, cf. .env.example), sinon webpack garde la branche et
// émet le chunk jusque dans un build prod ou natif. GetAppPage et InvitePage restent
// dehors : la première ne cache rien, la seconde porte son propre traitement du retour
// OAuth, que le useOAuthReturn de la gate lui volerait. L'app qui suit le rachat, elle,
// repasse sous la gate.
const StagingGate =
  process.env.REACT_APP_STAGING === 'true'
    ? React.lazy(() => import('./components/StagingGate'))
    : null;
const gated = (page) =>
  StagingGate ? (
    <React.Suspense fallback={null}>
      <StagingGate>{page}</StagingGate>
    </React.Suspense>
  ) : (
    page
  );

if (StudioPage) {
  root.render(
    <DataProvider>
      {gated(
        <React.Suspense fallback={null}>
          <StudioPage />
        </React.Suspense>
      )}
    </DataProvider>
  );
} else if (inviteCode || inviteOAuthReturn) {
  // La session anonyme est le support du chemin creation de compte
  // (updateUser({email}) la rend permanente) : sans elle, l'invite ne peut pas
  // s'inscrire. Non bloquant, le formulaire prend plus longtemps a remplir.
  ensureAnonSession();
  root.render(
    <DataProvider>
      <React.Suspense fallback={null}>
        <InvitePage />
      </React.Suspense>
    </DataProvider>
  );
} else if (isGetPage || isBrowserGated()) {
  // Meme page dans les deux cas. La difference est le texte : sur /get on invite
  // a installer, sur la gate on explique d'abord pourquoi le navigateur du
  // telephone ne sert pas l'app.
  root.render(<GetAppPage gated={!isGetPage} />);
} else {
  // Pose la session anonyme Supabase des le boot (prerequis du sync user), puis
  // configure RevenueCat avec ce user_id (natif seulement, no-op ailleurs).
  // Non bloquant : l'UI demarre offline-first sans attendre le reseau.
  ensureAnonSession().then(async (session) => {
    await configurePurchases(session?.user?.id);
  });

  root.render(
    <React.StrictMode>
      <DataProvider>{gated(<App />)}</DataProvider>
    </React.StrictMode>
  );
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
// Toujours desinscrit : le natif sert ses assets en local et n'a rien a gagner
// a un cache applicatif, et sur le web un service worker "cache d'abord" a fige
// des appareils sur un vieux bundle (cf. public/service-worker.js).
serviceWorker.unregister();

// If you want to start measuring performance in your app, pass a function
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
